import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Tabs,
  Tab,
  Snackbar,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  Card,
  CardContent,
} from '@mui/material';
import {
  Save as SaveIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  VerifiedUser as VerifiedUserIcon,
  Warning as WarningIcon,
  Help as HelpOutlineIcon,
  HourglassEmpty as HourglassIcon,
  AccountBalance as AccountBalanceIcon,
  CloudDone as CloudDoneIcon,
  CloudUpload as CloudUploadIcon,
  CloudOff as CloudOffIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// ── Helpers ────────────────────────────────────────────────────────────────

function ymd(d) {
  const dt = new Date(d);
  return dt.toISOString().slice(0, 10);
}

/** Next Sunday on/after the given date (week ending; week is Mon→Sun). */
function nextSunday(d = new Date()) {
  const dt = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const delta = (7 - dt.getDay()) % 7;
  dt.setDate(dt.getDate() + delta);
  return dt;
}

/**
 * Snap any date to the Sunday that closes its Mon→Sun week.
 *
 * Rule (Tom Clancy, 2026-07-13): whatever date a rep enters between
 * Monday and Sunday should be tagged to that week's Sunday. If they
 * pick a Sunday, it stays as-is. If they pick Mon–Sat, we roll forward
 * to the coming Sunday. Prevents reps from accidentally filing a Friday
 * or in-week date as the week_ending.
 *
 * Accepts a Date or an ISO 'YYYY-MM-DD' string. Returns a Date.
 */
function snapToSunday(input) {
  if (!input) return nextSunday(new Date());
  let dt;
  if (input instanceof Date) {
    dt = new Date(input.getFullYear(), input.getMonth(), input.getDate());
  } else {
    // Parse 'YYYY-MM-DD' as a local date (avoid the UTC-shift trap).
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(input));
    if (m) {
      dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    } else {
      const parsed = new Date(input);
      if (Number.isNaN(parsed.getTime())) return nextSunday(new Date());
      dt = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }
  }
  const delta = (7 - dt.getDay()) % 7;
  dt.setDate(dt.getDate() + delta);
  return dt;
}

/**
 * Default week_ending for a rep opening the form.
 *
 * Rule (Tom Clancy, 2026-07-06): a submission on Monday before 9:00 AM
 * local is reporting on the week that just ended — default to the
 * previous Sunday. Any other time, default to the next Sunday (the
 * currently-open reporting week).
 *
 * This matches how reps actually think about the form: Monday morning
 * is 'wrap up last week' time, not 'plan next week.'
 */
const MONDAY_CUTOFF_HOUR = 9; // 9:00 AM local
function defaultWeekEndingForNow(now = new Date()) {
  // Mon = 1 in JS getDay(). Before 9am on Monday → previous Sunday (yesterday).
  if (now.getDay() === 1 && now.getHours() < MONDAY_CUTOFF_HOUR) {
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    return yesterday; // that's a Sunday
  }
  return nextSunday(now);
}
// Back-compat alias — internal callers still reference nextFriday.
const nextFriday = defaultWeekEndingForNow;

function fmtPrettyDate(s) {
  if (!s) return '';
  const d = new Date(s);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

const EMPTY_ENTRY = {
  salesperson_name: '',
  new_proposals_count: 0,
  client_proposal_names: '',
  proposals_closed_count: 0,
  projects_closed_text: '',
  meetings_count: 0,
  meetings_text: '',
  partner_outreach_count: 0,
  partner_outreach_text: '',
  hootsuite_posts_count: 0,
  hootsuite_posts_text: '',
  other_bd_activities: '',
  top_wins: '',
  top_challenges: '',
};

// ── My Form (the salesperson view) ─────────────────────────────────────────

// Counts unique NN-NNNN job numbers in a free-text field. Mirrors
// the backend countJobNumbers() used for validation.
const JOB_NUMBER_RE = /\b\d{2}-\d{4}\b/g;
function countJobNumbers(text) {
  if (!text) return 0;
  const matches = text.match(JOB_NUMBER_RE);
  return matches ? new Set(matches).size : 0;
}

// ── Pipeline Review block ───────────────────────────────────────────────────
function PipelineReview({ userId, weekEnding, onValidityChange, onEntriesChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // state[dtools_id] = { status, note, touched }
  const [rowState, setRowState] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/weekly-activity/pipeline-review', {
        params: { week_ending: weekEnding, user_id: userId },
        timeout: 120000,
      });
      setData(res.data);
      // Seed row state:
      //  - existing (already saved this week) → keep as-is
      //  - carry (prior week within window) → keep STATUS, BLANK the note so
      //    the rep is forced to write a fresh update every week (Tom Clancy
      //    2026-10-01). The previous note is surfaced as placeholder/hint,
      //    not as the value, so the rep has to acknowledge the row.
      //  - none → blank
      const seed = {};
      for (const r of res.data.rows) {
        if (r.existing) {
          seed[r.dtools_id] = { status: r.existing.status, note: r.existing.note, touched: true, source: 'existing' };
        } else if (r.carry && r.carry.within_window) {
          seed[r.dtools_id] = {
            status: r.carry.status,
            note: '',
            touched: false,
            source: 'carry',
            priorNote: r.carry.note || '',
            priorWeekEnding: r.carry.week_ending || null,
          };
        } else {
          seed[r.dtools_id] = { status: '', note: '', touched: false, source: 'none' };
        }
      }
      setRowState(seed);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [userId, weekEnding]);

  useEffect(() => { load(); }, [load]);

  const setRow = (id, patch) => {
    setRowState((prev) => ({
      ...prev,
      [id]: { ...prev[id], ...patch, touched: true, source: 'edited' },
    }));
  };

  // Compute validation + flow to parent on every change
  const { entries, valid, summary } = useMemo(() => {
    if (!data) return { entries: [], valid: true, summary: null };
    const out = [];
    let allValid = true;
    let pending = 0, ok = 0, carriedClean = 0;
    for (const r of data.rows) {
      const s = rowState[r.dtools_id] || { status: '', note: '', source: 'none' };
      const noteLen = (s.note || '').trim().length;
      const rowValid = !!s.status && noteLen >= (data.min_note_length || 10);
      if (!rowValid) { allValid = false; pending++; } else { ok++; if (s.source === 'carry') carriedClean++; }
      out.push({
        dtools_id: r.dtools_id,
        dtools_number: r.dtools_number,
        name: r.name,
        client: r.client,
        price: r.price,
        progress: r.progress,
        status: s.status,
        note: s.note,
      });
    }
    return { entries: out, valid: allValid, summary: { total: data.rows.length, ok, pending, carriedClean } };
  }, [data, rowState]);

  useEffect(() => { onValidityChange && onValidityChange(valid); }, [valid, onValidityChange]);
  useEffect(() => { onEntriesChange && onEntriesChange(entries); }, [entries, onEntriesChange]);

  if (loading && !data) {
    return (
      <Paper sx={{ p: 3, mb: 2, textAlign: 'center' }}>
        <CircularProgress size={20} />
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          Fetching your pipeline from D-Tools… (30–60s)
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Alert severity="error" action={<Button size="small" onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      </Paper>
    );
  }

  if (!data) return null;

  if (data.rows.length === 0) {
    return (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Alert severity="info">
          No active proposals in D-Tools under your name ({data.aliases.join(', ') || 'no aliases mapped'}).
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Pipeline Review</Typography>
        <Chip
          icon={summary.pending === 0 ? <CheckCircleIcon /> : <WarningIcon />}
          label={summary.pending === 0
            ? `All ${summary.total} reviewed`
            : `${summary.pending} of ${summary.total} need attention`}
          color={summary.pending === 0 ? 'success' : 'warning'}
          size="small"
        />
        {summary.carriedClean > 0 && (
          <Chip
            size="small"
            variant="outlined"
            label={`${summary.carriedClean} carried from last week (≤${data.carry_forward_days}d)`}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refetch pipeline from D-Tools">
          <IconButton size="small" onClick={load} disabled={loading}>
            {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
        Every open proposal must be reviewed before you can submit. Pick a status and write a fresh
        note (≥{data.min_note_length} chars) — <strong>status carries forward from last week, but the note does not</strong>.
        You have to update us on each proposal every week.
      </Typography>

      <Stack spacing={1.5}>
        {data.rows.map((r) => {
          const s = rowState[r.dtools_id] || { status: '', note: '' };
          const noteLen = (s.note || '').trim().length;
          const rowValid = !!s.status && noteLen >= data.min_note_length;
          const carried = s.source === 'carry';
          return (
            <Paper key={r.dtools_id} variant="outlined" sx={{
              p: 1.5,
              borderLeft: rowValid ? '4px solid #2e7d32' : '4px solid #ed6c02',
              background: rowValid ? '#f6fdf7' : '#fffaf3',
            }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', md: 'flex-start' }}
              >
                <Box sx={{ flex: { xs: 'none', md: '0 0 220px' } }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {r.dtools_number || '(no #)'}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    {r.name}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    {r.client}
                  </Typography>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                    {r.progress && (
                      <Chip label={r.progress} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                    )}
                    {r.price != null && (
                      <Chip
                        label={new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(r.price)}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: 10 }}
                      />
                    )}
                    {carried && (
                      <Chip label="carried" size="small" color="info" sx={{ height: 18, fontSize: 10 }} />
                    )}
                  </Stack>
                </Box>
                <FormControl size="small" sx={{ minWidth: { xs: 0, md: 200 }, width: { xs: '100%', md: 'auto' } }} fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={s.status || ''}
                    onChange={(e) => setRow(r.dtools_id, { status: e.target.value })}
                    error={!s.status}
                  >
                    {data.statuses.map((opt) => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ flex: 1, width: { xs: '100%', md: 'auto' } }}>
                  <TextField
                    size="small"
                    label={carried ? 'This week’s update (required)' : 'Note'}
                    value={s.note || ''}
                    onChange={(e) => setRow(r.dtools_id, { note: e.target.value })}
                    multiline
                    minRows={1}
                    maxRows={4}
                    fullWidth
                    error={!s.note || s.note.trim().length < data.min_note_length}
                    placeholder={carried ? 'What changed since last week? (required every week)' : ''}
                    helperText={
                      noteLen === 0
                        ? `Required every week (≥${data.min_note_length} chars). Status carries forward; the note does not.`
                        : noteLen < data.min_note_length
                          ? `Need ${data.min_note_length - noteLen} more chars`
                          : ''
                    }
                  />
                  {carried && s.priorNote && (
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', color: 'text.secondary', mt: 0.5, pl: 0.5, fontStyle: 'italic' }}
                    >
                      Last week ({s.priorWeekEnding ? new Date(s.priorWeekEnding).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }) : 'prior'}): “{s.priorNote}”
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Paper>
  );
}

// Small status indicator shown next to the Submit button so the rep
// always knows whether their in-progress work is safe.
function SaveStatusChip({ state, submitted, lastSavedAt }) {
  // state: 'idle' | 'dirty' | 'saving' | 'saved' | 'error'
  if (submitted && state !== 'dirty' && state !== 'saving' && state !== 'error') {
    return (
      <Chip
        size="small"
        icon={<CheckCircleIcon />}
        label="Submitted"
        color="success"
        variant="outlined"
      />
    );
  }
  if (state === 'saving') {
    return (
      <Chip
        size="small"
        icon={<CloudUploadIcon />}
        label="Saving draft…"
        color="default"
        variant="outlined"
      />
    );
  }
  if (state === 'error') {
    return (
      <Tooltip title="Last autosave failed. We'll retry on your next edit.">
        <Chip
          size="small"
          icon={<CloudOffIcon />}
          label="Draft not saved"
          color="warning"
          variant="outlined"
        />
      </Tooltip>
    );
  }
  if (state === 'dirty') {
    return (
      <Chip
        size="small"
        icon={<CloudUploadIcon />}
        label="Unsaved changes…"
        color="default"
        variant="outlined"
      />
    );
  }
  // saved or idle
  const when = lastSavedAt
    ? `Draft saved · ${new Date(lastSavedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
    : 'Draft';
  return (
    <Chip
      size="small"
      icon={<CloudDoneIcon />}
      label={when}
      color="info"
      variant="outlined"
    />
  );
}

function MyForm({ user, onSubmitted }) {
  const [weekEnding, setWeekEnding] = useState(() => ymd(nextFriday()));
  const [entry, setEntry] = useState({ ...EMPTY_ENTRY, salesperson_name: user?.name || '' });
  const [existingId, setExistingId] = useState(null);
  // Was the loaded row already submitted (vs. just a draft)?
  const [submittedStatus, setSubmittedStatus] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  // Autosave state. The actual debounce + save lives in a useEffect below.
  const [saveState, setSaveState] = useState('idle'); // idle|dirty|saving|saved|error
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // hydratingRef gates the autosave — we don't want to autosave the
  // values we just loaded from the server.
  const hydratingRef = useRef(true);
  // userEditedRef stays false until the rep actually touches the form.
  // Guards against a phantom empty draft getting written to the DB when
  // async children (e.g. PipelineReview finishing its D-Tools fetch)
  // trigger our autosave effect before any real user input.
  // Ryan Wood 2026-07-06 saw a blank auto-submit before he'd typed a thing —
  // this is what stops it. Any real edit (update/updateNum/pipeline change)
  // flips this to true; nothing else does.
  const userEditedRef = useRef(false);
  const saveTimerRef = useRef(null);
  const inFlightRef = useRef(false);
  const pendingSaveRef = useRef(false);

  const load = useCallback(async (we) => {
    setLoading(true);
    hydratingRef.current = true;
    try {
      const res = await api.get('/weekly-activity/entry', { params: { week_ending: we } });
      if (res.data.row) {
        const r = res.data.row;
        setEntry({
          salesperson_name: r.salesperson_name || user?.name || '',
          new_proposals_count: r.new_proposals_count ?? 0,
          client_proposal_names: r.client_proposal_names || '',
          proposals_closed_count: r.proposals_closed_count ?? 0,
          projects_closed_text: r.projects_closed_text || '',
          meetings_count: r.meetings_count ?? 0,
          meetings_text: r.meetings_text || '',
          partner_outreach_count: r.partner_outreach_count ?? 0,
          partner_outreach_text: r.partner_outreach_text || '',
          hootsuite_posts_count: r.hootsuite_posts_count ?? 0,
          hootsuite_posts_text: r.hootsuite_posts_text || '',
          other_bd_activities: r.other_bd_activities || '',
          top_wins: r.top_wins || '',
          top_challenges: r.top_challenges || '',
        });
        setExistingId(r.id);
        setSubmittedStatus(r.status === 'submitted');
        setSaveState('saved');
        setLastSavedAt(r.last_edited_at || r.submitted_at || null);
      } else {
        setEntry({ ...EMPTY_ENTRY, salesperson_name: user?.name || '' });
        setExistingId(null);
        setSubmittedStatus(false);
        setSaveState('idle');
        setLastSavedAt(null);
      }
    } catch (e) {
      setToast({ severity: 'error', message: e.response?.data?.error || e.message });
    } finally {
      setLoading(false);
      // Switching weeks or reloading always resets the edited flag —
      // whatever the rep typed in a prior week doesn't count as edits
      // to the newly-loaded week's form.
      userEditedRef.current = false;
      // Allow autosave to fire on the *next* user edit, not on the
      // setEntry call we just made.
      setTimeout(() => { hydratingRef.current = false; }, 50);
    }
  }, [user]);

  useEffect(() => { load(weekEnding); }, [weekEnding, load]);

  const update = (field) => (e) => {
    userEditedRef.current = true;
    setEntry((p) => ({ ...p, [field]: e.target.value }));
  };
  const updateNum = (field) => (e) => {
    userEditedRef.current = true;
    const v = e.target.value;
    setEntry((p) => ({ ...p, [field]: v === '' ? '' : Math.max(0, parseInt(v, 10) || 0) }));
  };

  // Wrapper for the pipeline review's onEntriesChange. We can't tell from
  // the child alone whether an entry change came from user input or from
  // the child's initial hydration (which seeds row state from prior-week
  // carry-forward + existing rows). So we compare against the previous
  // entries and only mark 'edited' when a status or note actually changed
  // from what the user last saw.
  const prevPipelineSigRef = useRef(null);
  const handlePipelineEntriesChange = useCallback((next) => {
    const sig = JSON.stringify((next || []).map(r => [r.dtools_id, r.status || '', r.note || '']));
    if (prevPipelineSigRef.current !== null && prevPipelineSigRef.current !== sig) {
      userEditedRef.current = true;
    }
    prevPipelineSigRef.current = sig;
    setPipelineEntries(next);
  }, []);

  // Pipeline review state — child component owns the rows; we just track
  // overall validity + the entries it would POST.
  const [pipelineValid, setPipelineValid] = useState(true);
  const [pipelineEntries, setPipelineEntries] = useState([]);
  const pipelineEntriesRef = useRef(pipelineEntries);
  useEffect(() => { pipelineEntriesRef.current = pipelineEntries; }, [pipelineEntries]);

  // Core autosave: write current form + pipeline to /draft endpoints.
  // No validation, no toasts on success. If it fails we surface a quiet
  // "Draft not saved" chip and retry on the next edit.
  const autosaveDraft = useCallback(async () => {
    if (inFlightRef.current) {
      // A save is already running; queue another after it returns.
      pendingSaveRef.current = true;
      return;
    }
    inFlightRef.current = true;
    setSaveState('saving');
    try {
      await api.post('/weekly-activity/entry/draft', { week_ending: weekEnding, ...entry });
      // Only ship pipeline review rows that the rep has actually touched.
      const pe = pipelineEntriesRef.current.filter(
        (r) => (r.status && r.status.length > 0) || (r.note && r.note.length > 0)
      );
      if (pe.length > 0) {
        await api.post('/weekly-activity/pipeline-review/draft', {
          week_ending: weekEnding,
          entries: pe,
        });
      }
      setSaveState('saved');
      setLastSavedAt(new Date().toISOString());
    } catch (err) {
      setSaveState('error');
      // Intentionally no toast — don't spam the user mid-typing.
      // eslint-disable-next-line no-console
      console.warn('[weekly-activity] autosave failed:', err.response?.data?.error || err.message);
    } finally {
      inFlightRef.current = false;
      if (pendingSaveRef.current) {
        pendingSaveRef.current = false;
        // Run the queued save on next tick so React state is settled.
        setTimeout(() => autosaveDraft(), 0);
      }
    }
  }, [entry, weekEnding]);

  // Debounced autosave: any change to `entry` or `pipelineEntries`
  // schedules a save ~1.5s after the rep stops typing.
  //
  // Two gates before we schedule anything:
  //   1. hydratingRef — we're still processing the initial server load.
  //   2. userEditedRef — the rep hasn't actually typed anything yet.
  // Without gate #2 we can end up writing a blank draft the moment an
  // async child (PipelineReview) finishes hydrating and pushes its rows
  // up. That's how Ryan ended up with a 7:41am ghost row on 2026-07-06.
  useEffect(() => {
    if (hydratingRef.current) return;
    if (!userEditedRef.current) return;
    setSaveState((s) => (s === 'saving' ? s : 'dirty'));
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      autosaveDraft();
    }, 1500);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [entry, pipelineEntries, autosaveDraft]);

  // Flush the debounced save the moment the rep switches tabs or
  // hides the window. Catches the very common "I'll come back to this
  // later" pattern that used to lose data. Same guard as the main
  // autosave effect — don't flush a blank form the rep never touched.
  useEffect(() => {
    const flush = () => {
      if (!userEditedRef.current) return;
      if (saveTimerRef.current && saveState === 'dirty') {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
        autosaveDraft();
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    const onPageHide = () => flush();
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('blur', flush);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('blur', flush);
    };
  }, [saveState, autosaveDraft]);

  // Warn the rep if they try to close the tab with unsaved edits still
  // pending. The autosave usually fires within 1.5s so this only kicks
  // in if they close *immediately* after typing.
  useEffect(() => {
    const handler = (e) => {
      if (saveState === 'dirty' || saveState === 'saving') {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [saveState]);

  const submit = useCallback(async () => {
    setSaving(true);
    setFieldErrors({});
    // Cancel any pending debounced draft — the explicit submit supersedes it.
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    try {
      await api.post('/weekly-activity/entry', { week_ending: weekEnding, ...entry });
      // Save pipeline review entries after the main submission succeeds.
      if (pipelineEntries.length > 0) {
        await api.post('/weekly-activity/pipeline-review', {
          week_ending: weekEnding,
          entries: pipelineEntries,
        });
      }
      setToast({ severity: 'success', message: submittedStatus ? 'Updated.' : 'Submitted. Thanks!' });
      setSaveState('saved');
      setLastSavedAt(new Date().toISOString());
      await load(weekEnding);
      onSubmitted && onSubmitted();
    } catch (e) {
      if (e.response?.status === 422 && e.response?.data?.field_errors) {
        setFieldErrors(e.response.data.field_errors);
        setToast({ severity: 'error', message: e.response.data.error || 'Validation failed.' });
      } else if (e.response?.status === 422 && e.response?.data?.row_errors) {
        setToast({ severity: 'error', message: e.response.data.error || 'Pipeline review validation failed.' });
      } else {
        setToast({ severity: 'error', message: e.response?.data?.error || e.message });
      }
    } finally {
      setSaving(false);
    }
  }, [entry, weekEnding, submittedStatus, load, onSubmitted, pipelineEntries]);

  // Live client-side validation mirroring the server rules. We don't
  // block typing; we surface a helper line and disable Submit when the
  // numbers don't line up.
  const liveErrors = useMemo(() => {
    const errors = {};
    const propCount = Number(entry.new_proposals_count) || 0;
    if (propCount > 0) {
      const found = countJobNumbers(entry.client_proposal_names);
      if (found < propCount) {
        errors.client_proposal_names = `${propCount} proposal${propCount === 1 ? '' : 's'} reported, ${found} job number${found === 1 ? '' : 's'} found. Add a job number (e.g. 26-0518) for each one.`;
      }
    }
    const closeCount = Number(entry.proposals_closed_count) || 0;
    if (closeCount > 0) {
      const found = countJobNumbers(entry.projects_closed_text);
      if (found < closeCount) {
        errors.projects_closed_text = `${closeCount} close${closeCount === 1 ? '' : 's'} reported, ${found} job number${found === 1 ? '' : 's'} found. Add a job number (e.g. 26-0518) for each one.`;
      }
    }
    return errors;
  }, [entry.new_proposals_count, entry.client_proposal_names, entry.proposals_closed_count, entry.projects_closed_text]);

  // ── Real-time WIP check on closed-row jobs ──────────────────────────────
  // Tom Clancy 2026-10-01: a job may only be reported as closed if D-Tools
  // shows it as 'Work In Progress'. We check in real time as the rep types
  // and offer a one-click 'Move to Top Wins' for any job that isn't WIP.
  // wipMap[jobNumber] = { found, wip, dtools_progress?, project_name?, client_name? }
  const [wipMap, setWipMap] = useState({});
  const [wipChecking, setWipChecking] = useState(false);
  useEffect(() => {
    const jobs = Array.from(new Set((entry.projects_closed_text || '').match(/\b\d{2}-\d{4}\b/g) || []));
    if (jobs.length === 0) { setWipMap({}); return; }
    let cancelled = false;
    setWipChecking(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/weekly-activity/wip-check', { params: { jobs: jobs.join(',') } });
        if (!cancelled) setWipMap(res.data?.results || {});
      } catch (e) {
        // Silent — don't nag the rep over infra blips. Submit path re-checks server-side.
      } finally {
        if (!cancelled) setWipChecking(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [entry.projects_closed_text]);

  // Any job the rep listed as 'closed' that is NOT Work-In-Progress in D-Tools.
  const nonWipClosedJobs = useMemo(() => {
    const jobs = Array.from(new Set((entry.projects_closed_text || '').match(/\b\d{2}-\d{4}\b/g) || []));
    return jobs.filter((j) => {
      const r = wipMap[j];
      if (!r) return false; // not checked yet — don't flag prematurely
      return !r.wip;
    });
  }, [entry.projects_closed_text, wipMap]);

  // Move a single job number out of "Projects/Proposals Closed" and into
  // "Top Wins" with a [verbal] prefix. Cuts the matching line(s) wholesale
  // so the rep's descriptive text comes along for the ride.
  const moveClosedJobToWins = useCallback((jobNumber) => {
    const srcLines = (entry.projects_closed_text || '').split(/\r?\n/);
    const moved = [];
    const kept = [];
    for (const line of srcLines) {
      if (line.includes(jobNumber)) moved.push(line);
      else kept.push(line);
    }
    if (moved.length === 0) return;
    const movedBlock = moved
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => (l.toLowerCase().startsWith('[verbal]') ? l : `[verbal] ${l}`))
      .join('\n');
    const existingWins = (entry.top_wins || '').replace(/\s+$/, '');
    const newWins = existingWins ? `${existingWins}\n${movedBlock}` : movedBlock;
    // Decrement the closed count by the number of lines we moved out, floor at 0.
    const newCount = Math.max(0, (Number(entry.proposals_closed_count) || 0) - moved.length);
    setEntry((p) => ({
      ...p,
      projects_closed_text: kept.join('\n').replace(/\n{3,}/g, '\n\n').trim(),
      proposals_closed_count: newCount,
      top_wins: newWins,
    }));
    // Clear any server-side error on the closed field — the rep is actively fixing it.
    setFieldErrors((prev) => {
      if (!prev.projects_closed_text) return prev;
      const { projects_closed_text, ...rest } = prev;
      return rest;
    });
  }, [entry.projects_closed_text, entry.top_wins, entry.proposals_closed_count]);

  // Combined live errors (counts + WIP gate).
  const liveErrorsWithWip = useMemo(() => {
    const errors = { ...liveErrors };
    if (nonWipClosedJobs.length > 0) {
      errors.projects_closed_text =
        `These job${nonWipClosedJobs.length === 1 ? '' : 's'} are NOT Work-In-Progress in D-Tools and cannot be reported as closed: ${nonWipClosedJobs.join(', ')}. ` +
        `Use the “Move to Top Wins” button next to each one, or remove them.`;
    }
    return errors;
  }, [liveErrors, nonWipClosedJobs]);

  // Server errors win over live errors (they're more authoritative; they only
  // appear after a real submit).
  const displayErrors = { ...liveErrorsWithWip, ...fieldErrors };
  const hasErrors = Object.keys(displayErrors).length > 0 || !pipelineValid || nonWipClosedJobs.length > 0;

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <TextField
            label="Week Ending (Sunday)"
            type="date"
            value={weekEnding}
            onChange={(e) => {
              // Snap whatever the rep picks to the enclosing Mon→Sun week's Sunday.
              // Empty value = leave as-is so the picker doesn't fight the rep mid-edit.
              const raw = e.target.value;
              if (!raw) { setWeekEnding(raw); return; }
              setWeekEnding(ymd(snapToSunday(raw)));
            }}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
            helperText="Any date you pick will snap to that week's Sunday."
            sx={{ flex: { xs: 1, sm: 'none' }, minWidth: { sm: 200 } }}
          />
          {submittedStatus ? (
            <Chip icon={<CheckCircleIcon />} label="Already submitted — edit below" color="success" />
          ) : existingId ? (
            <Chip icon={<CloudDoneIcon />} label="Draft in progress — not yet submitted" color="info" />
          ) : (
            <Chip label="Not yet submitted for this week" color="warning" />
          )}
          <SaveStatusChip state={saveState} submitted={submittedStatus} lastSavedAt={lastSavedAt} />
          <Box sx={{ flex: { xs: 0, sm: 1 } }} />
          <Tooltip title="Reload">
            <IconButton onClick={() => load(weekEnding)} disabled={loading} sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}>
              {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
          Reports cover Monday–Sunday and are due by Monday morning. Your work autosaves as a draft as you type — nothing is shared with management until you click Submit.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Stack spacing={3}>
          <TextField
            label="Salesperson Name"
            value={entry.salesperson_name}
            onChange={update('salesperson_name')}
            fullWidth
            size="small"
          />

          {/* Proposals written */}
          <Box>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <TextField
                label="How many NEW proposals did you write this week?"
                type="number"
                value={entry.new_proposals_count}
                onChange={updateNum('new_proposals_count')}
                inputProps={{ min: 0 }}
                sx={{ width: 360 }}
              />
            </Stack>
            <TextField
              label="Client Proposal Names (one per line — include job number NN-NNNN)"
              value={entry.client_proposal_names}
              onChange={update('client_proposal_names')}
              fullWidth
              multiline
              minRows={3}
              sx={{ mt: 1 }}
              placeholder="e.g. 26-0518 - Silo Ridge (Alvarez) - Service Contract + ACS Care"
              required={Number(entry.new_proposals_count) > 0}
              error={!!displayErrors.client_proposal_names}
              helperText={displayErrors.client_proposal_names || 'Job number is required for each proposal so we can verify against D-Tools / Sage.'}
            />
          </Box>

          <Divider />

          {/* Closed */}
          <Box>
            <TextField
              label="How many projects/proposals did you close this week?"
              type="number"
              value={entry.proposals_closed_count}
              onChange={updateNum('proposals_closed_count')}
              inputProps={{ min: 0 }}
              sx={{ width: 360 }}
            />
            <TextField
              label="Projects/Proposals Closed (one per line — include job number NN-NNNN)"
              value={entry.projects_closed_text}
              onChange={update('projects_closed_text')}
              fullWidth
              multiline
              minRows={3}
              sx={{ mt: 1 }}
              placeholder="NA if none. Otherwise: job number + client + scope, e.g. 26-0512 - 54 MacDougal St (Schloss) - AV"
              required={Number(entry.proposals_closed_count) > 0}
              error={!!displayErrors.projects_closed_text}
              helperText={displayErrors.projects_closed_text || 'Only Work-In-Progress jobs in D-Tools may be reported as closed. Verbal approvals go in “Top Wins”.'}
            />
            {/* Per-job WIP badges + one-click move-to-wins */}
            {Object.keys(wipMap).length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                {wipChecking && <CircularProgress size={14} />}
                {Object.entries(wipMap).map(([job, info]) => {
                  if (info.wip) {
                    return (
                      <Tooltip key={job} title={`D-Tools: Work In Progress — ${info.project_name || ''} ${info.client_name ? '(' + info.client_name + ')' : ''}`}>
                        <Chip
                          size="small"
                          color="success"
                          icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                          label={`${job} — WIP ✓`}
                        />
                      </Tooltip>
                    );
                  }
                  const reason = !info.found
                    ? 'Not found in D-Tools'
                    : `D-Tools status: ${info.dtools_progress || 'unknown'}`;
                  return (
                    <Chip
                      key={job}
                      size="small"
                      color="error"
                      icon={<WarningIcon sx={{ fontSize: 14 }} />}
                      label={`${job} — ${reason} → Move to Top Wins`}
                      onClick={() => moveClosedJobToWins(job)}
                      clickable
                    />
                  );
                })}
              </Box>
            )}
          </Box>

          <Divider />

          {/* Meetings */}
          <Box>
            <TextField
              label="How many Client/Partner Meetings did you attend this week?"
              type="number"
              value={entry.meetings_count}
              onChange={updateNum('meetings_count')}
              inputProps={{ min: 0 }}
              sx={{ width: 360 }}
            />
            <TextField
              label="Client/Partner Meetings (one per line)"
              value={entry.meetings_text}
              onChange={update('meetings_text')}
              fullWidth
              multiline
              minRows={3}
              sx={{ mt: 1 }}
            />
          </Box>

          <Divider />

          {/* Partner outreach */}
          <Box>
            <TextField
              label="How many new designers, architects, builders or clients did you engage this week?"
              type="number"
              value={entry.partner_outreach_count}
              onChange={updateNum('partner_outreach_count')}
              inputProps={{ min: 0 }}
              sx={{ width: 360 }}
            />
            <TextField
              label="Partner Outreach (one per line — name + firm)"
              value={entry.partner_outreach_text}
              onChange={update('partner_outreach_text')}
              fullWidth
              multiline
              minRows={3}
              sx={{ mt: 1 }}
            />
          </Box>

          <Divider />

          {/* Social posts & marketing efforts */}
          <Box>
            <TextField
              label="How many social media posts did you make this week (Hootsuite or other)?"
              type="number"
              value={entry.hootsuite_posts_count}
              onChange={updateNum('hootsuite_posts_count')}
              inputProps={{ min: 0 }}
              sx={{ width: 360 }}
            />
            <TextField
              label="Social Posts & Other Marketing Efforts"
              value={entry.hootsuite_posts_text}
              onChange={update('hootsuite_posts_text')}
              fullWidth
              multiline
              minRows={3}
              sx={{ mt: 1 }}
              placeholder={'List Hootsuite posts AND any other marketing activity to promote ACS this week. NA if none.\n\nExamples:\n• Hootsuite — LinkedIn post on Silo Ridge theater install\n• Attended CEDIA Northeast chapter mixer (12 integrators)\n• Hosted designer lunch-and-learn at the Westbury showroom (8 attendees)\n• Sent personal outreach emails to 5 architects on recent project list\n• Co-branded Instagram reel w/ Lutron rep\n• Logged client referral to ACS marketing team'}
              helperText="Hootsuite posts and ANY other marketing effort you did to actively promote ACS this week."
            />
          </Box>

          <Divider />

          <TextField
            label="Other business development activities"
            value={entry.other_bd_activities}
            onChange={update('other_bd_activities')}
            fullWidth
            multiline
            minRows={3}
          />

          <TextField
            label="Top 3 Wins"
            value={entry.top_wins}
            onChange={update('top_wins')}
            fullWidth
            multiline
            minRows={3}
          />

          <TextField
            label="Top 3 Challenges"
            value={entry.top_challenges}
            onChange={update('top_challenges')}
            fullWidth
            multiline
            minRows={3}
          />

        </Stack>
      </Paper>

      <PipelineReview
        userId={user?.id}
        weekEnding={weekEnding}
        onValidityChange={setPipelineValid}
        onEntriesChange={handlePipelineEntriesChange}
      />

      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <SaveStatusChip state={saveState} submitted={submittedStatus} lastSavedAt={lastSavedAt} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Your work autosaves as a draft. Click Submit to finalize and send to management.
            </Typography>
          </Box>
          <Box>
            {hasErrors && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Fix the items flagged above before submitting. Job numbers (NN-NNNN format)
                are required for every proposal and close, and every pipeline proposal needs a
                status + note. (Your draft is still being saved automatically.)
              </Alert>
            )}
            <Button
              variant="contained"
              size="large"
              startIcon={saving ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <SaveIcon />}
              onClick={submit}
              disabled={saving || hasErrors}
            >
              {submittedStatus ? 'Resubmit (update final report)' : 'Submit Final Report'}
            </Button>
          </Box>
        </Stack>
      </Paper>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast && (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
}

// ── Admin (consolidated view) ──────────────────────────────────────────────

function AdminPanel() {
  const [weekEnding, setWeekEnding] = useState(() => ymd(nextFriday()));
  const [weeks, setWeeks] = useState([]);
  const [data, setData] = useState(null);
  const [verifyData, setVerifyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [toast, setToast] = useState(null);

  const loadWeeks = useCallback(async () => {
    try {
      const res = await api.get('/weekly-activity/weeks');
      setWeeks(res.data.rows || []);
    } catch (_) {}
  }, []);
  const load = useCallback(async (we) => {
    setLoading(true);
    try {
      const [d, v] = await Promise.all([
        api.get('/weekly-activity', { params: { week_ending: we } }),
        api.get('/weekly-activity/verify', { params: { week_ending: we } }).catch(() => ({ data: null })),
      ]);
      setData(d.data);
      setVerifyData(v.data);
    } catch (e) {
      setToast({ severity: 'error', message: e.response?.data?.error || e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadWeeks(); }, [loadWeeks]);
  useEffect(() => { load(weekEnding); }, [weekEnding, load]);

  const exportXlsx = useCallback(async () => {
    try {
      const res = await api.get('/weekly-activity/export.xlsx', {
        params: { week_ending: weekEnding },
        responseType: 'blob',
      });
      const mmddyy = weekEnding.slice(5, 7) + weekEnding.slice(8, 10) + weekEnding.slice(2, 4);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `WeekEnding${mmddyy}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setToast({ severity: 'error', message: e.response?.data?.error || e.message });
    }
  }, [weekEnding]);

  const [pdfBusy, setPdfBusy] = useState(false);
  const exportPdf = useCallback(async () => {
    setPdfBusy(true);
    try {
      const res = await api.get('/weekly-activity/export.pdf', {
        params: { week_ending: weekEnding },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const d = new Date(weekEnding + 'T00:00:00Z');
      const tag = `${d.getUTCMonth() + 1}-${d.getUTCDate()}-${d.getUTCFullYear()}`;
      const a = document.createElement('a');
      a.href = url;
      a.download = `ACS_Weekly_Sales_Activity_Report_Week_Ending_${tag}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // Blob error responses don't expose JSON — read it back.
      let msg = e.message;
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).error || msg; } catch (_) {}
      } else {
        msg = e.response?.data?.error || msg;
      }
      setToast({ severity: 'error', message: msg });
    } finally {
      setPdfBusy(false);
    }
  }, [weekEnding]);

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{ flexWrap: 'wrap', width: '100%' }}
        >
          <FormControl
            size="small"
            sx={{
              flex: { xs: '1 1 100%', md: '0 0 320px' },
              minWidth: { xs: 0, md: 240 },
              maxWidth: { md: 360 },
            }}
          >
            <InputLabel>Week Ending</InputLabel>
            <Select
              label="Week Ending"
              value={weekEnding}
              onChange={(e) => setWeekEnding(e.target.value)}
            >
              {/* Always include the default (next Sunday) so admins can prep the current week */}
              {!weeks.find((w) => ymd(w.week_ending) === weekEnding) && (
                <MenuItem value={weekEnding}>
                  {fmtPrettyDate(weekEnding)} — 0 submitted
                </MenuItem>
              )}
              {weeks.map((w) => (
                <MenuItem key={w.week_ending} value={ymd(w.week_ending)}>
                  {fmtPrettyDate(w.week_ending)} — {w.submitted_count} submitted
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Or jump to date"
            type="date"
            value={weekEnding}
            onChange={(e) => setWeekEnding(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{
              flex: { xs: '1 1 100%', md: '0 0 170px' },
              minWidth: { md: 160 },
              maxWidth: { md: 200 },
            }}
          />
          <Stack direction="row" spacing={1} sx={{ ml: { md: 'auto' }, width: { xs: '100%', md: 'auto' }, flexWrap: 'wrap' }}>
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => load(weekEnding)}
              disabled={loading}
              sx={{ flex: { xs: 1, md: 'none' } }}
            >
              Refresh
            </Button>
            <Box sx={{ flex: { xs: 0, md: 1 } }} />
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportXlsx}
              disabled={!data || data.counts?.submitted === 0}
              sx={{ flex: { xs: 1, md: 'none' } }}
            >
              <Box sx={{ display: { xs: 'inline', md: 'none' } }}>xlsx</Box>
              <Box sx={{ display: { xs: 'none', md: 'inline' } }}>
                Download xlsx
              </Box>
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportPdf}
              disabled={!data || data.counts?.submitted === 0 || pdfBusy}
              sx={{ flex: { xs: 1, md: 'none' } }}
            >
              <Box sx={{ display: { xs: 'inline', md: 'none' } }}>{pdfBusy ? 'Building…' : 'PDF'}</Box>
              <Box sx={{ display: { xs: 'none', md: 'inline' } }}>
                {pdfBusy ? 'Building PDF…' : 'Download PDF Report'}
              </Box>
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {loading && <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>}

      {data && !loading && (
        <>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            gap: 1.5,
            mb: 2,
          }}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Submitted</Typography>
                <Typography variant="h4" sx={{ fontSize: { xs: 22, sm: 28 } }}>{data.counts.submitted} / {data.counts.eligible}</Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Proposals written</Typography>
                <Typography variant="h4" sx={{ fontSize: { xs: 22, sm: 28 } }}>
                  {data.submissions.reduce((s, r) => s + (r.new_proposals_count || 0), 0)}
                </Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Closed</Typography>
                <Typography variant="h4" sx={{ fontSize: { xs: 22, sm: 28 } }}>
                  {data.submissions.reduce((s, r) => s + (r.proposals_closed_count || 0), 0)}
                </Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Meetings</Typography>
                <Typography variant="h4" sx={{ fontSize: { xs: 22, sm: 28 } }}>
                  {data.submissions.reduce((s, r) => s + (r.meetings_count || 0), 0)}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {data.missing.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }} icon={<CancelIcon />}>
              <strong>Not yet submitted:</strong>{' '}
              {data.missing.map((m) => m.name).join(', ')}
            </Alert>
          )}

          <Paper>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Salesperson</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Proposals</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Closed</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Meetings</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Outreach</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Hootsuite</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Submitted</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>&nbsp;</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.submissions.map((r) => (
                    <React.Fragment key={r.id}>
                      <TableRow hover>
                        <TableCell>{r.salesperson_name}</TableCell>
                        <TableCell align="right">{r.new_proposals_count}</TableCell>
                        <TableCell align="right">{r.proposals_closed_count}</TableCell>
                        <TableCell align="right">{r.meetings_count}</TableCell>
                        <TableCell align="right">{r.partner_outreach_count}</TableCell>
                        <TableCell align="right">{r.hootsuite_posts_count}</TableCell>
                        <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                          {new Date(r.submitted_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => setExpanded((p) => ({ ...p, [r.id]: !p[r.id] }))}>
                            {expanded[r.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      {expanded[r.id] && (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ background: '#fafafa', py: 2 }}>
                            {verifyData && (() => {
                              const v = verifyData.results.find((x) => x.submission_id === r.id);
                              return v ? <VerificationBlock result={v} /> : null;
                            })()}
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                              <DetailField label="Client Proposal Names" value={r.client_proposal_names} />
                              <DetailField label="Projects/Proposals Closed" value={r.projects_closed_text} />
                              <DetailField label="Client/Partner Meetings" value={r.meetings_text} />
                              <DetailField label="Partner Outreach" value={r.partner_outreach_text} />
                              <DetailField label="Hootsuite Posts" value={r.hootsuite_posts_text} />
                              <DetailField label="Other BD Activities" value={r.other_bd_activities} />
                              <DetailField label="Top 3 Wins" value={r.top_wins} />
                              <DetailField label="Top 3 Challenges" value={r.top_challenges} />
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                  {data.submissions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                        No submissions yet for this week.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast && (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
}

// ── Verification overlay component ──────────────────────────────────────────────
const fmtUSD = (v) => {
  if (v == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(v));
};

function JobChip({ entry, mode }) {
  // mode: 'proposal' | 'close' | 'meeting'
  let color = 'default', icon = <HelpOutlineIcon sx={{ fontSize: 14 }} />, label = entry.job_number;
  if (!entry.matched) {
    color = 'error';
    icon = <WarningIcon sx={{ fontSize: 14 }} />;
    label = entry.job_number + ' (unknown)';
  } else if (mode === 'close') {
    if (entry.sage_confirmed) { color = 'success'; icon = <CheckCircleIcon sx={{ fontSize: 14 }} />; label = entry.job_number + ' — Sage ✓'; }
    else { color = 'warning'; icon = <HourglassIcon sx={{ fontSize: 14 }} />; label = entry.job_number + ' — pending Sage'; }
  } else {
    color = 'success';
    icon = <CheckCircleIcon sx={{ fontSize: 14 }} />;
    label = entry.job_number;
  }
  return (
    <Tooltip title={
      entry.matched
        ? `${entry.project_name || ''}\n${entry.client_name || ''}\nSalesman: ${entry.sales_rep || '—'}${mode === 'close' && entry.sage_total ? `\nSage posting total: ${fmtUSD(entry.sage_total)}` : ''}`
        : 'No matching project number in Command Center. Either too new, typo, or not yet synced from D-Tools.'
    }>
      <Chip
        icon={icon}
        size="small"
        color={color}
        variant={entry.matched ? 'filled' : 'outlined'}
        label={label}
        sx={{ m: 0.25, fontFamily: 'monospace', fontSize: 11 }}
      />
    </Tooltip>
  );
}

function VerificationBlock({ result }) {
  const s = result.summary;
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1, background: '#f8fafc' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <VerifiedUserIcon sx={{ fontSize: 16, color: 'primary.main' }} />
        <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary' }}>
          Verification overlay
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Chip size="small" variant="outlined" label={`${s.proposals_matched}/${s.proposals_parsed} proposals matched`} />
        <Chip size="small" variant="outlined" color={s.closes_confirmed === s.closes_parsed && s.closes_parsed > 0 ? 'success' : 'default'} label={`${s.closes_confirmed}/${s.closes_parsed} closes confirmed in Sage`} />
        {s.proposals_drift !== 0 && (
          <Chip size="small" color="warning" label={`Self-reported ${result.reported_counts.proposals}, parsed ${s.proposals_parsed}`} />
        )}
      </Stack>

      {result.proposals.length > 0 && (
        <Box sx={{ mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', mr: 1 }}>Proposals:</Typography>
          {result.proposals.map((p) => <JobChip key={'p' + p.job_number} entry={p} mode="proposal" />)}
        </Box>
      )}
      {result.closes.length > 0 && (
        <Box sx={{ mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', mr: 1 }}>Closes:</Typography>
          {result.closes.map((c) => <JobChip key={'c' + c.job_number} entry={c} mode="close" />)}
        </Box>
      )}
      {result.meetings.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', mr: 1 }}>Meetings (jobs mentioned):</Typography>
          {result.meetings.map((m) => <JobChip key={'m' + m.job_number} entry={m} mode="meeting" />)}
        </Box>
      )}

      {result.proposals.length === 0 && result.closes.length === 0 && result.meetings.length === 0 && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          No project numbers detected in this submission. Reps should include the
          job number (e.g. <code>26-0518</code>) so we can match against Sage / D-Tools.
        </Typography>
      )}
    </Paper>
  );
}

function RepPipelinePanel({ userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    api.get('/weekly-activity/rep-pipeline', { params: { user_id: userId } })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>;
  if (!data) return null;

  // Top 10 by lifetime unpaid (proxy for AR until Pat confirms definition)
  const withUnpaid = data.rows.filter((r) => Number(r.lifetime_unpaid) > 100);
  withUnpaid.sort((a, b) => Number(b.lifetime_unpaid) - Number(a.lifetime_unpaid));
  const top = withUnpaid.slice(0, 10);

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1, background: '#fffbeb' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <AccountBalanceIcon sx={{ fontSize: 16, color: 'warning.dark' }} />
        <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary' }}>
          {data.user_name}'s top jobs by lifetime unpaid (billed − paid)
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {data.rows.length} active jobs total
        </Typography>
      </Stack>
      {top.length === 0 ? (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          No active jobs with lifetime unpaid balances.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: 11, fontWeight: 600 }}>Job</TableCell>
              <TableCell sx={{ fontSize: 11, fontWeight: 600 }}>Client</TableCell>
              <TableCell sx={{ fontSize: 11, fontWeight: 600 }} align="right">Contract</TableCell>
              <TableCell sx={{ fontSize: 11, fontWeight: 600 }} align="right">Billed</TableCell>
              <TableCell sx={{ fontSize: 11, fontWeight: 600 }} align="right">Paid</TableCell>
              <TableCell sx={{ fontSize: 11, fontWeight: 600 }} align="right">Lifetime Unpaid</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {top.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{r.primary_job_number}</TableCell>
                <TableCell sx={{ fontSize: 11, maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.client_name}</TableCell>
                <TableCell sx={{ fontSize: 11, fontFamily: 'monospace' }} align="right">{fmtUSD(r.sage_contract_amount)}</TableCell>
                <TableCell sx={{ fontSize: 11, fontFamily: 'monospace' }} align="right">{fmtUSD(r.billed)}</TableCell>
                <TableCell sx={{ fontSize: 11, fontFamily: 'monospace' }} align="right">{fmtUSD(r.paid)}</TableCell>
                <TableCell sx={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 600, color: 'warning.dark' }} align="right">{fmtUSD(r.lifetime_unpaid)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Alert severity="info" sx={{ mt: 1, fontSize: 11, py: 0.25 }} icon={false}>
        <strong>Note (preview):</strong> "Lifetime unpaid" = <code>sage_billed − sage_paid</code>.
        This is NOT the same as Sage's true Open AR (currently $1.4M company-wide via
        <code> vw_TMB_Open_AR_by_Job</code>). Pending Pat's confirmation on the definition
        before we surface this number on the live page.
      </Alert>
    </Paper>
  );
}

function DetailField({ label, value }) {
  if (!value) return null;
  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: 11 }}>
        {label}
      </Typography>
      <Box component="pre" sx={{
        fontFamily: 'inherit',
        fontSize: 13,
        whiteSpace: 'pre-wrap',
        m: 0,
        mt: 0.5,
      }}>{value}</Box>
    </Box>
  );
}

// ── My History ─────────────────────────────────────────────────────────────

function MyHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/weekly-activity/mine');
      setRows(res.data.rows || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>;

  if (rows.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
        <AssignmentIcon sx={{ fontSize: 48, color: '#ccc' }} />
        <Typography variant="body2" sx={{ mt: 1 }}>
          You haven't submitted any weekly reports yet.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 640 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Week Ending</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Proposals</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Closed</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Meetings</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Outreach</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Hootsuite</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Submitted</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{fmtPrettyDate(r.week_ending)}</TableCell>
                <TableCell align="right">{r.new_proposals_count}</TableCell>
                <TableCell align="right">{r.proposals_closed_count}</TableCell>
                <TableCell align="right">{r.meetings_count}</TableCell>
                <TableCell align="right">{r.partner_outreach_count}</TableCell>
                <TableCell align="right">{r.hootsuite_posts_count}</TableCell>
                <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {new Date(r.submitted_at).toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

export default function WeeklyActivity() {
  const { user } = useAuth();
  const isMgmt = user?.role === 'Management' || [1, 2, 3, 4, 8, 19, 90].includes(user?.id);
  // Bill Hipp (id 8) submits a weekly report alongside the Sales reps.
  const EXTRA_SALES_IDS = [8];
  const isSales = user?.role === 'Sales'
    || (Array.isArray(user?.roles) && user.roles.includes('Sales'))
    || EXTRA_SALES_IDS.includes(user?.id);

  // Default tab: salespeople land on "My Form"; managers land on "All Submissions"
  const [tab, setTab] = useState(isSales && !isMgmt ? 'mine' : (isMgmt ? 'admin' : 'mine'));

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1300, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <AssignmentIcon fontSize="large" sx={{ color: 'primary.main' }} />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Weekly Sales Activity</Typography>
      </Stack>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Submit once per week. Week runs Mon–Sun; submissions due by Monday morning. Replaces the Zoho Sales Activity form.
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #e0e0e0', mb: 2 }}>
        {(isSales || isMgmt) && <Tab value="mine" label="My Form" icon={<EditIcon />} iconPosition="start" />}
        {(isSales || isMgmt) && <Tab value="history" label="My History" />}
        {isMgmt && <Tab value="admin" label="All Submissions" />}
      </Tabs>

      {tab === 'mine' && <MyForm user={user} />}
      {tab === 'history' && <MyHistory />}
      {tab === 'admin' && isMgmt && <AdminPanel />}
    </Box>
  );
}
